import { createUseStyles } from "react-jss";
import { ITheme } from "../../../constants/theme";
import {
  BreakPointDesktop,
  BreakPointMobile,
  BreakPointTablet,
} from "../../../constants/dimension";
import { isTV } from "../../../utils/platform";

const ARROW_SIZE = 36;
const AVATAR_FOCUS_RING = 4;
// Nine 100px avatars with 24px gaps
const CONTENT_MAX_WIDTH = 1092;
const DESKTOP_GUTTER = 90;

const desktopColumn = {
  width: `calc(100% - ${DESKTOP_GUTTER * 2}px)`,
  maxWidth: CONTENT_MAX_WIDTH,
  marginInline: "auto",
};

// ArrowWrapper hides its arrows below desktop; this page shows them there
const scrollableRow = (theme: ITheme) => ({
  marginInline: -theme.spacing.space16,
  paddingInline: theme.spacing.space16,
  "& > [role=button]": {
    display: "flex !important",
    width: `${ARROW_SIZE}px !important`,
    minWidth: `${ARROW_SIZE}px !important`,
    height: `${ARROW_SIZE}px !important`,
    alignSelf: "center",
  },
  "& > [role=button]:first-child": {
    marginRight: -(ARROW_SIZE + theme.spacing.space16),
  },
  "& > [role=button]:last-child": {
    marginLeft: -(ARROW_SIZE + theme.spacing.space16),
  },
});

export const useStyles = createUseStyles((theme: ITheme) => ({
  page: {
    position: "fixed",
    inset: 0,
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none",
    margin: 0,
    border: "none",
    outline: "none",
    padding: `0 ${theme.spacing.space16}px ${theme.spacing.space24}px`,
    boxSizing: "border-box",
    flexDirection: "column",
    gap: theme.spacing.space8,
    overflowX: "hidden",
    overflowY: "auto",
    overscrollBehavior: "contain",
    color: theme.colors.TextDefault,
    background: `radial-gradient(ellipse at 100% 0%, rgba(58, 22, 74, 0.6) 0%, transparent 55%), linear-gradient(180deg, #000 0%, ${theme.colors.Background1} 100%)`,
    "&[open]": {
      display: "flex",
    },
    "&::backdrop": {
      backgroundColor: "#000",
    },
    "&::-webkit-scrollbar": {
      display: "none",
    },
    [BreakPointDesktop]: {
      padding: `0 0 ${theme.spacing.space40}px`,
      gap: 56,
    },
  },
  header: {
    position: "relative",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 72,
    [BreakPointDesktop]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing.space40,
      minHeight: 0,
      padding: `${theme.spacing.space40}px ${DESKTOP_GUTTER}px 0`,
    },
  },
  backButton: {
    position: "absolute",
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    borderRadius: theme.radius.small,
    cursor: "pointer",
    [BreakPointDesktop]: {
      position: "static",
      background: "rgba(255, 255, 255, 0.10)",
      ...(!isTV && { backdropFilter: "blur(24.5px)" }),
      width: 96,
      height: 96,
      padding: theme.spacing.space8,
      boxSizing: "border-box",
      borderRadius: theme.radius.large - theme.radius.sSmall,
    },
  },
  backIcon: {
    [BreakPointDesktop]: {
      fontSize: "48px !important",
    },
  },
  backButtonSelected: {
    boxShadow: `0 0 0 2px ${theme.colors.TextDefault}`,
  },
  title: {
    textAlign: "center",
    paddingInline: 48,
    [BreakPointMobile]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
      fontWeight: `${theme.fontWeight.regular} !important`,
    },
    [BreakPointTablet]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
      fontWeight: `${theme.fontWeight.regular} !important`,
    },
    [BreakPointDesktop]: {
      ...desktopColumn,
      paddingInline: 0,
      textAlign: "left",
      fontSize: "32px !important",
      lineHeight: "45px !important",
    },
  },
  bundleList: {
    display: "flex",
    flexDirection: "column",
    // Visible gaps from Figma minus the avatar row focus-ring padding
    gap: theme.spacing.space24 - AVATAR_FOCUS_RING,
    width: "100%",
    paddingBottom: theme.spacing.space24,
    [BreakPointDesktop]: {
      ...desktopColumn,
      gap: theme.spacing.space40 - AVATAR_FOCUS_RING,
      paddingBottom: 0,
    },
  },
  bundle: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.space12 - AVATAR_FOCUS_RING,
    minWidth: 0,
    [BreakPointDesktop]: {
      gap: theme.spacing.space24 - AVATAR_FOCUS_RING,
    },
  },
  avatarList: {
    display: "flex",
    gap: theme.spacing.space16,
    minWidth: 0,
    boxSizing: "border-box",
    overflowX: "auto",
    overflowY: "hidden",
    padding: AVATAR_FOCUS_RING,
    "&::-webkit-scrollbar": {
      display: "none",
    },
    // Touch layouts bleed rows to the screen edge and scroll with arrows
    [BreakPointMobile]: scrollableRow(theme),
    [BreakPointTablet]: scrollableRow(theme),
    // Desktop/TV fits a full bundle on one line, no scrolling
    [BreakPointDesktop]: {
      gap: theme.spacing.space24,
      // Scaled margin (marginInline is not) plus right slack so nine avatars always fit
      margin: `0 -${AVATAR_FOCUS_RING * 2}px 0 -${AVATAR_FOCUS_RING}px`,
      padding: AVATAR_FOCUS_RING,
      overflowX: "visible",
      overflowY: "visible",
      // Overrides the touch rules where breakpoints overlap (e.g. 932x430)
      "& > [role=button]": {
        display: "none !important",
      },
    },
  },
  avatar: {
    position: "relative",
    flex: "0 0 auto",
    width: 100,
    height: 100,
    [BreakPointTablet]: {
      width: 80,
      height: 80,
    },
    [BreakPointMobile]: {
      width: 80,
      height: 80,
    },
    borderRadius: "50%",
    overflow: "hidden",
    cursor: "pointer",
  },
  avatarActive: {
    boxShadow: "0 0 0 3px #1877EB",
  },
  avatarSelected: {
    boxShadow: `0 0 0 3px ${theme.colors.TextDefault}`,
  },
  avatarImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));
